import { inject } from 'mobx-react';
import * as React from 'react';

import { ClientLogger } from 'bernie-client';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { withLocalization } from 'bernie-l10n';

import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { SearchCriteriaModel } from 'stores/models';

import { DateRangeProps, DateRangeState } from 'components/common/search-form/fields/date-range';
import { FormFieldBinding } from 'components/common/search-form/fields/form-field-binding';
import { NewDateField } from 'components/common/search-form/fields/new-date-field';

const logger = ClientLogger.getLoggerWithIdentifier('pwa.component.searchform');

@withLocalization
@FormFieldBinding
@inject('analytics')
export class PartialStayDateRange extends React.Component<DateRangeProps, DateRangeState> {
  constructor(props) {
    super(props);

    this.state = {
      endDateFocus: undefined,
      startDateFocus: undefined,
    };

    this.handleChange = this.handleChange.bind(this);
    this.registerFocusMethod = this.registerFocusMethod.bind(this);
    this.validate = this.validate.bind(this);

    const { registerValidator } = props;

    registerValidator(() => this.validate('startDate', true));
    registerValidator(() => this.validate('endDate', true));
  }

  public render() {
    const { l10n, model, autoSubmit, isSubmitted } = this.props;
    const { formatText } = l10n;

    const validPartialStart = model.partialStayStartDate && this.isValidDate(new Date(model.partialStayStartDate));
    const validPartialEnd = model.partialStayEndDate && this.isValidDate(new Date(model.partialStayEndDate));

    const startDate = validPartialStart ? model.partialStayStartDate : model.startDate;
    const endDate = validPartialEnd ? model.partialStayEndDate : model.endDate;

    const buttonText = autoSubmit ? formatText('hotels.search.form.submit.label') : formatText('hotels.roomPicker.doneButton.label');

    const wrapperClasses = 'uitk-cell s-cell-1-1 m-cell-2-2 l-cell-4-4 xl-cell-4-4';
    const startDateError = this.validate('startDate', isSubmitted);
    const endDateError = this.validate('endDate', isSubmitted);

    const commonDateFieldProps = {
      startDate,
      endDate,
      onChange: this.handleChange,
      placeholder: formatText('hotels.search.form.toDate.placeholder'),
      numDaysBookableInAdvance: SearchCriteriaModel.NUM_OF_DAYS_BOOKABLE_IN_ADVANCE,
      midnightBookingOffsetHours: SearchCriteriaModel.MIDNIGHT_BOOKING_OFFSET_IN_HOURS,
      buttonText,
      registerFocusMethod: this.registerFocusMethod,
      isDateless: false,
      updateModels: this.updateModels.bind(this),
    };

    if (logger && typeof logger.logEvent === 'function' && (startDateError || endDateError)) {
      this.logDateRangeError(startDateError, endDateError, startDate, endDate);
    }

    const routerLinkWrapper = children => <>{children}</>;

    const startDateLabel = formatText('hotels.search.form.fromDateHyphen.label');
    const endDateLabel = formatText('hotels.search.form.toDateHyphen.label');

    const dateFieldProps = {
      dateFieldWrapperClasses: wrapperClasses,
      endId: 'hotels-partial-stay-check-out',
      endInvalid: endDateError && endDateError.text,
      endLabel: endDateLabel,
      endName: 'partialStayEndDate',
      endPlaceholder: formatText('hotels.search.form.toDate.placeholder'),
      onChange: commonDateFieldProps.onChange,
      routerLinkWrapper,
      startId: 'hotels-partial-stay-check-in',
      startInvalid: startDateError && startDateError.text,
      startLabel: startDateLabel,
      startName: 'partialStayStartDate',
      startPlaceholder: formatText('hotels.search.form.toDate.placeholder'),
    };

    const rfrr = {
      end: ':ChangePartialDatesCheckOut',
      start: ':ChangePartialDatesCheckIn',
    };

    return (
      model.isPartialStay && (
        <Viewport>
          <ViewSmall>
            <div className="uitk-cell s-cell-1-1">
              <NewDateField rfrr={rfrr} dateFieldProps={dateFieldProps} {...commonDateFieldProps} />
            </div>
          </ViewSmall>
          <ViewLarge>
            <React.Fragment>
              <div className="uitk-cell all-cell-2-3">
                <NewDateField rfrr={rfrr} dateFieldProps={dateFieldProps} {...commonDateFieldProps} />
              </div>
              <div className="uitk-cell l-cell-1-3 xl-cell-1-3" />
            </React.Fragment>
          </ViewLarge>
        </Viewport>
      )
    );
  }

  private updateModels(startDate, endDate) {
    this.handleChange({
      target: {
        name: 'partialStayStartDate',
        value: startDate,
      },
    });

    this.handleChange({
      target: {
        name: 'partialStayEndDate',
        value: endDate,
      },
    });
  }

  private handleChange(event) {
    const { updateModel } = this.props;

    if (typeof updateModel === 'function') {
      updateModel(event);
    }
  }

  private logDateRangeError(startDateError, endDateError, startDate, endDate) {
    logger.logEvent(
      new SystemEvent(SystemEventLevel.WARN, 'SEARCH_FORM__PARTIAL_STAY_DATE_RANGE_ERROR'),
      `${startDateError} | ${endDateError}`,
      `partialStayStartDate=${startDate} partialStayEndDate=${endDate}`
    );
  }

  private registerFocusMethod(fieldName: string, focusMethod: () => void) {
    this.setState({ [`${fieldName}Focus`]: focusMethod });
  }

  // eslint-disable-next-line complexity
  private validate(key, isSubmitted) {
    const { l10n, model, showErrorValidationOnSubmit } = this.props;
    const { startDateFocus, endDateFocus } = this.state;

    const { formatText } = l10n;

    const { partialStayStartDate, partialStayEndDate, startDate, endDate, isPartialStay } = model;
    // Dont validate if partial stay isnt selected
    if (!isPartialStay) {
      return;
    }

    const start = partialStayStartDate ? partialStayStartDate : startDate;
    const end = partialStayEndDate ? partialStayEndDate : startDate;

    if (key === 'startDate') {
      const partialStart = start && new Date(start);
      const searchStartDate = startDate && new Date(startDate);
      const message =
        isSubmitted && showErrorValidationOnSubmit
          ? 'hotels.search.validation.invalid.dateless.text'
          : 'hotels.search.validation.invalidFromDate.text';

      if (!this.isValidDate(partialStart) && (!model.isDateless || (isSubmitted && showErrorValidationOnSubmit))) {
        return {
          text: formatText(message),
          focus: startDateFocus,
        };
      }

      searchStartDate.setHours(0, 0, 0, 0);
      partialStart.setHours(0, 0, 0, 0);

      if (partialStart < searchStartDate) {
        return {
          text: formatText('hotels.search.validation.invalidPartialDates.text'),
          focus: endDateFocus,
        };
      }
    }

    if (key === 'endDate') {
      const searchEndDate = endDate && new Date(endDate);
      const partialStart = start && new Date(start);
      const partialEnd = end && new Date(end);
      const message =
        isSubmitted && showErrorValidationOnSubmit
          ? 'hotels.search.validation.invalid.dateless.text'
          : 'hotels.search.validation.invalidToDate.text';

      if (!this.isValidDate(partialEnd) && (!model.isDateless || (isSubmitted && showErrorValidationOnSubmit))) {
        return {
          text: formatText(message),
          focus: endDateFocus,
        };
      }

      if (this.isValidDate(partialStart)) {
        if (partialEnd < partialStart) {
          return {
            text: formatText('hotels.search.validation.invalidDateRange.text'),
            focus: endDateFocus,
          };
        }

        searchEndDate.setHours(0, 0, 0, 0);
        partialEnd.setHours(0, 0, 0, 0);
        partialStart.setHours(0, 0, 0, 0);

        if (partialEnd > searchEndDate) {
          return {
            text: formatText('hotels.search.validation.invalidPartialDates.text'),
            focus: endDateFocus,
          };
        }
      }
    }
  }

  private isValidDate(date: Date) {
    return date && date.toString() !== 'Invalid Date';
  }
}
